<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NodeLibrary from './components/NodeLibrary.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FlowNode from './components/FlowNode.vue';
import NodeSettings from './components/NodeSettings.vue';
import FlowSettings from './components/FlowSettings.vue';
import {
  attachNode,
  buildStepNode,
  countNodes,
  findNode,
  findParent,
  isDescendant,
  moveNode,
} from './components/treeOps';
import {
  isLifecycleTriggerConfigurationValid,
  lifecycleAllStagesFromSettings,
  serializeLifecycleTriggerSettings,
} from './components/lifecycleTriggerSettings';

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const flowId = computed(() => route.params.flowId);
const isEdit = computed(() => Boolean(flowId.value));

const form = reactive({
  name: '',
  trigger_type: 'conversation_opened',
  once_per_contact: false,
  lifecycle_all_stages: true,
  lifecycle_stage_ids: [],
  lifecycle_trigger_when_cleared: false,
  inbox_ids: [],
  trigger_conditions: [],
  schedule: null,
  tree: null,
});
// Full settings as loaded, so saving merges over them instead of replacing
// the object (an API-set key must survive a builder round-trip).
let loadedSettings = {};
const selectedNodeId = ref(null);
const insertTargetId = ref(null);
const saving = ref(false);
const lifecycleStages = computed(
  () => store.getters['customerLifecycle/getActiveStages'] || []
);
const lifecycleEnabled = computed(
  () =>
    Boolean(isTeletelFeatureEnabled('lifecycle')) &&
    store.getters['customerLifecycle/isReady']
);
const lifecycleTriggerValid = computed(() =>
  isLifecycleTriggerConfigurationValid({
    triggerType: form.trigger_type,
    allStages: form.lifecycle_all_stages,
    stageIds: form.lifecycle_stage_ids,
    triggerWhenCleared: form.lifecycle_trigger_when_cleared,
  })
);
const canSave = computed(
  () => Boolean(form.name.trim()) && lifecycleTriggerValid.value
);

const loadLifecycleStages = async () => {
  if (!isTeletelFeatureEnabled('lifecycle')) return;
  try {
    const settings = await store.dispatch('customerLifecycle/getSettings');
    if (settings?.available && settings?.enabled) {
      await store.dispatch('customerLifecycle/getStages');
    }
  } catch {
    // The flow builder still supports non-lifecycle triggers and nodes when
    // the optional lifecycle feature is unavailable.
  }
};

watch(() => isTeletelFeatureEnabled('lifecycle'), loadLifecycleStages, {
  immediate: true,
});

const blankTree = triggerType => ({
  id: 'root',
  pId: null,
  name: 'Trigger',
  customType: 'Trigger',
  data: { type: triggerType },
  childs: [],
});

watch(
  () => form.trigger_type,
  triggerType => {
    if (form.tree?.customType === 'Trigger') {
      form.tree.data = { ...(form.tree.data || {}), type: triggerType };
    }
  }
);

// An empty canvas has exactly one place a step can go: under the trigger. Arm
// it up front rather than making the user discover the insert marker first -
// otherwise the whole step library renders disabled while CANVAS_EMPTY tells
// them to pick a step from it, which is a contradiction the user has to guess
// their way out of.
const armRootIfEmpty = () => {
  if (form.tree && !form.tree.childs.length)
    insertTargetId.value = form.tree.id;
};

onMounted(async () => {
  if (!isEdit.value) {
    form.tree = blankTree(form.trigger_type);
    armRootIfEmpty();
    return;
  }
  const flow = await store.dispatch('teletelFlows/show', flowId.value);
  form.name = flow.name;
  form.trigger_type = flow.trigger_type;
  form.once_per_contact = Boolean(flow.settings?.once_per_contact);
  form.lifecycle_stage_ids = [...(flow.settings?.lifecycle_stage_ids || [])];
  form.lifecycle_all_stages = lifecycleAllStagesFromSettings(
    flow.settings || {}
  );
  form.lifecycle_trigger_when_cleared = Boolean(
    flow.settings?.lifecycle_trigger_when_cleared
  );
  form.inbox_ids = [...(flow.settings?.inbox_ids || [])];
  loadedSettings = JSON.parse(JSON.stringify(flow.settings || {}));
  form.trigger_conditions = JSON.parse(
    JSON.stringify(flow.settings?.trigger_conditions || [])
  );
  form.schedule = flow.settings?.schedule
    ? JSON.parse(JSON.stringify(flow.settings.schedule))
    : null;
  form.tree =
    flow.tree && flow.tree.id
      ? JSON.parse(JSON.stringify(flow.tree))
      : blankTree(flow.trigger_type);
  armRootIfEmpty();
});

// ---- dragging a step to a new place in the tree -----------------------------
// Pointer events, not HTML5 drag-and-drop. The canvas sits under a
// `transform: scale()`, and native dragging is unreliable beneath a transformed
// ancestor - drags frequently never start at all, which is exactly what was
// reported. Pointer events are unaffected by the transform, and
// document.elementFromPoint resolves through it correctly.
const DRAG_THRESHOLD = 5;
const draggingId = ref(null);
const dropTargetId = ref(null);
let pending = null;
let dragListeners = null;

const dropIdAt = (x, y) => {
  const el = document.elementFromPoint(x, y);
  const slot = el?.closest?.('[data-drop-id]');
  return slot?.dataset.dropId || null;
};

function onPointerMoveDrag(e) {
  if (!pending) return;

  if (!draggingId.value) {
    // Wait for real movement so a click still selects rather than drags.
    const moved =
      Math.abs(e.clientX - pending.x) + Math.abs(e.clientY - pending.y);
    if (moved < DRAG_THRESHOLD) return;
    draggingId.value = pending.id;
    // An armed insert point would compete with the drop slots.
    insertTargetId.value = null;
  }

  const id = dropIdAt(e.clientX, e.clientY);
  // Never light up a slot the move would be refused for.
  dropTargetId.value =
    id &&
    id !== draggingId.value &&
    !isDescendant(form.tree, draggingId.value, id)
      ? id
      : null;
}

// One controller drops every listener at once, so ending a drag never has to
// name the handlers - which would force a definition order they cannot both
// satisfy, since the pointerup handler finishes by calling this.
function endDrag() {
  pending = null;
  draggingId.value = null;
  dropTargetId.value = null;
  dragListeners?.abort();
  dragListeners = null;
}

function onPointerUpDrag(e) {
  const dragId = draggingId.value;
  const targetId = dragId ? dropIdAt(e.clientX, e.clientY) : null;
  endDrag();
  if (!dragId || !targetId) return;
  if (!moveNode(form.tree, dragId, targetId)) {
    useAlert(t('TELETEL_FLOWS.BUILDER.MOVE_REFUSED'));
    return;
  }
  selectedNodeId.value = dragId;
  armRootIfEmpty();
}

const onNodePointerDown = e => {
  if (e.button !== 0) return;
  const card = e.target.closest?.('[data-node-id]');
  if (!card) return;
  pending = { id: card.dataset.nodeId, x: e.clientX, y: e.clientY };
  dragListeners = new AbortController();
  const { signal } = dragListeners;
  window.addEventListener('pointermove', onPointerMoveDrag, { signal });
  window.addEventListener('pointerup', onPointerUpDrag, { signal });
  window.addEventListener('pointercancel', endDrag, { signal });
};

onBeforeUnmount(endDrag);

// ---- canvas zoom & pan -----------------------------------------------------
// A tree of any depth outgrows a fixed pane, so the canvas gets a transform
// layer instead of relying on scrollbars. Deliberately hand-rolled: swapping in
// a graph library would mean converting the nested `childs` tree to flat
// nodes+edges and back on every save, and that round-trip is where flows would
// silently lose branches.
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2;
const zoom = ref(1);
const pan = reactive({ x: 0, y: 0 });
const canvasRef = ref(null);
const panning = ref(false);
let panStart = null;

const clampZoom = z => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const zoomLabel = computed(() => `${Math.round(zoom.value * 100)}%`);

const setZoom = (next, origin) => {
  const z = clampZoom(next);
  if (z === zoom.value) return;
  // Keep the point under the cursor fixed, otherwise zooming drifts the tree
  // off-screen and the user has to hunt for it.
  if (origin) {
    const k = z / zoom.value;
    pan.x = origin.x - (origin.x - pan.x) * k;
    pan.y = origin.y - (origin.y - pan.y) * k;
  }
  zoom.value = z;
};

const zoomIn = () => setZoom(zoom.value + 0.1);
const zoomOut = () => setZoom(zoom.value - 0.1);
const resetView = () => {
  zoom.value = 1;
  pan.x = 0;
  pan.y = 0;
};

const onWheel = e => {
  // Plain wheel keeps scrolling the pane; only an intentional ctrl/⌘ + wheel
  // zooms, which is what every canvas tool trains people to expect.
  if (!e.ctrlKey && !e.metaKey) return;
  e.preventDefault();
  const rect = canvasRef.value?.getBoundingClientRect();
  const origin = rect
    ? { x: e.clientX - rect.left, y: e.clientY - rect.top }
    : null;
  setZoom(zoom.value - Math.sign(e.deltaY) * 0.1, origin);
};

const onPanStart = e => {
  // Middle button, or left button on empty canvas - never on a node, or
  // dragging to pan would fight with selecting.
  const onBlank = e.target === canvasRef.value || e.target.dataset?.canvasBlank;
  if (e.button !== 1 && !(e.button === 0 && onBlank)) return;
  panning.value = true;
  panStart = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  e.preventDefault();
};

const onPanMove = e => {
  if (!panning.value || !panStart) return;
  pan.x = e.clientX - panStart.x;
  pan.y = e.clientY - panStart.y;
};

const onPanEnd = () => {
  panning.value = false;
  panStart = null;
};

const nodeCount = computed(() => countNodes(form.tree));
const selectedNode = computed(() =>
  selectedNodeId.value ? findNode(form.tree, selectedNodeId.value) : null
);

const selectNode = id => {
  selectedNodeId.value = id;
  insertTargetId.value = null;
  armRootIfEmpty();
};
const requestInsert = parentId => {
  insertTargetId.value = parentId;
};

const addStep = type => {
  const parentId = insertTargetId.value;
  if (!parentId) return;
  const parent = findNode(form.tree, parentId);
  if (!parent) return;
  const node = buildStepNode(type);
  // Same placement rules as a dragged step, so inserting and moving cannot
  // disagree about what a valid tree looks like. This used to be duplicated
  // here and carried the same defect: a branching step inserted above an
  // existing subtree became a second child of a linear parent, which the canvas
  // hides and the server rejects.
  if (!attachNode(form.tree, parentId, node)) {
    useAlert(t('TELETEL_FLOWS.BUILDER.INSERT_REFUSED'));
    return;
  }
  insertTargetId.value = null;
  selectedNodeId.value = node.id;
};

const deleteNode = id => {
  const parent = findParent(form.tree, id);
  if (!parent) return;
  const index = parent.childs.findIndex(c => c.id === id);
  const node = parent.childs[index];
  // Linear nodes promote their single child; anything else removes the
  // subtree (branch containers keep the flow grammar intact server-side).
  if (
    node.childs.length === 1 &&
    !['QuestionStep', 'BranchStep'].includes(node.customType)
  ) {
    parent.childs.splice(index, 1, node.childs[0]);
  } else {
    parent.childs.splice(index, 1);
  }
  selectedNodeId.value = null;
  armRootIfEmpty();
};

const save = async () => {
  if (!lifecycleTriggerValid.value) {
    useAlert(t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_STAGE_REQUIRED'));
    return false;
  }
  saving.value = true;
  try {
    const settings = {
      ...loadedSettings,
      once_per_contact: form.once_per_contact,
      ...serializeLifecycleTriggerSettings({
        allStages: form.lifecycle_all_stages,
        stageIds: form.lifecycle_stage_ids,
        triggerWhenCleared: form.lifecycle_trigger_when_cleared,
      }),
      inbox_ids: form.inbox_ids,
      trigger_conditions: form.trigger_conditions,
    };
    if (form.schedule) settings.schedule = form.schedule;
    const payload = {
      name: form.name,
      trigger_type: form.trigger_type,
      tree: form.tree,
      settings,
    };
    if (isEdit.value) {
      await store.dispatch('teletelFlows/update', {
        id: flowId.value,
        ...payload,
      });
    } else {
      const flow = await store.dispatch('teletelFlows/create', payload);
      router.replace({
        name: 'teletel_flows_edit',
        params: { flowId: flow.id },
      });
    }
    useAlert(t('TELETEL_FLOWS.ACTIONS.SAVE_DONE'));
    return true;
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
    return false;
  } finally {
    saving.value = false;
  }
};

const publish = async () => {
  const saved = await save();
  if (!saved) return;
  try {
    await store.dispatch('teletelFlows/publish', flowId.value || undefined);
    useAlert(t('TELETEL_FLOWS.ACTIONS.PUBLISH_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 p-4 gap-3 overflow-hidden">
    <div class="flex items-center gap-3 flex-wrap">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{
          isEdit
            ? t('TELETEL_FLOWS.BUILDER.TITLE_EDIT')
            : t('TELETEL_FLOWS.BUILDER.TITLE_NEW')
        }}
      </h1>
      <input
        v-model="form.name"
        class="w-64 mb-0 text-sm"
        :placeholder="t('TELETEL_FLOWS.BUILDER.NAME_LABEL')"
      />
      <select
        v-model="form.trigger_type"
        class="w-56 mb-0 text-sm"
        :disabled="isEdit"
      >
        <option value="conversation_opened">
          {{ t('TELETEL_FLOWS.TRIGGERS.conversation_opened') }}
        </option>
        <option value="conversation_closed">
          {{ t('TELETEL_FLOWS.TRIGGERS.conversation_closed') }}
        </option>
        <option value="contact_field_updated">
          {{ t('TELETEL_FLOWS.TRIGGERS.contact_field_updated') }}
        </option>
        <option value="contact_tag_updated">
          {{ t('TELETEL_FLOWS.TRIGGERS.contact_tag_updated') }}
        </option>
        <option
          v-if="lifecycleEnabled || form.trigger_type === 'lifecycle_updated'"
          value="lifecycle_updated"
        >
          {{ t('TELETEL_FLOWS.TRIGGERS.lifecycle_updated') }}
        </option>
        <option value="csat_submitted">
          {{ t('TELETEL_FLOWS.TRIGGERS.csat_submitted') }}
        </option>
        <option value="incoming_webhook">
          {{ t('TELETEL_FLOWS.TRIGGERS.incoming_webhook') }}
        </option>
        <option value="shortcut">
          {{ t('TELETEL_FLOWS.TRIGGERS.shortcut') }}
        </option>
      </select>
      <label class="flex items-center gap-1 text-sm text-n-slate-11">
        <input v-model="form.once_per_contact" type="checkbox" class="mb-0" />
        {{ t('TELETEL_FLOWS.BUILDER.FORM.ONCE_PER_CONTACT') }}
      </label>
      <template v-if="form.trigger_type === 'lifecycle_updated'">
        <label class="flex items-center gap-1 text-sm text-n-slate-11">
          <input
            v-model="form.lifecycle_all_stages"
            type="checkbox"
            class="mb-0"
          />
          {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_ALL_STAGES') }}
        </label>
        <select
          v-if="!form.lifecycle_all_stages"
          v-model="form.lifecycle_stage_ids"
          multiple
          class="min-h-20 w-56 mb-0 text-sm text-start"
          :aria-label="t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_TRIGGER_STAGES')"
        >
          <option
            v-for="stage in lifecycleStages"
            :key="stage.id"
            :value="stage.id"
          >
            {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
          </option>
        </select>
        <p
          v-if="!lifecycleTriggerValid"
          class="mb-0 text-xs text-n-ruby-11"
          role="alert"
        >
          {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_STAGE_REQUIRED') }}
        </p>
        <label class="flex items-center gap-1 text-sm text-n-slate-11">
          <input
            v-model="form.lifecycle_trigger_when_cleared"
            type="checkbox"
            class="mb-0"
          />
          {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_WHEN_CLEARED') }}
        </label>
      </template>
      <span class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.NODE_LIMIT', { count: nodeCount }) }}
      </span>
      <div class="flex gap-2 ms-auto">
        <NextButton
          sm
          :is-loading="saving"
          :disabled="saving || !canSave"
          @click="save"
        >
          {{ t('TELETEL_FLOWS.ACTIONS.SAVE') }}
        </NextButton>
        <NextButton
          v-if="isEdit"
          teal
          sm
          :is-loading="saving"
          :disabled="saving || !canSave"
          @click="publish"
        >
          {{ t('TELETEL_FLOWS.ACTIONS.PUBLISH') }}
        </NextButton>
      </div>
    </div>

    <div class="flex flex-1 min-h-0 gap-3">
      <aside
        class="w-52 shrink-0 overflow-auto border rounded-xl border-n-weak p-3"
      >
        <h2 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.BUILDER.LIBRARY') }}
        </h2>
        <NodeLibrary
          :insert-armed="Boolean(insertTargetId)"
          :lifecycle-enabled="lifecycleEnabled"
          @pick="addStep"
        />
      </aside>

      <main
        ref="canvasRef"
        data-canvas-blank="true"
        class="relative flex-1 overflow-hidden rounded-xl bg-n-solid-1"
        :class="panning ? 'cursor-grabbing' : 'cursor-grab'"
        @wheel="onWheel"
        @pointerdown="onNodePointerDown"
        @mousedown="onPanStart"
        @mousemove="onPanMove"
        @mouseup="onPanEnd"
        @mouseleave="onPanEnd"
      >
        <div
          class="absolute top-0 origin-top p-6 will-change-transform"
          :style="{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }"
        >
          <FlowNode
            v-if="form.tree"
            :node="form.tree"
            :selected-id="selectedNodeId"
            :insert-target-id="insertTargetId"
            :dragging-id="draggingId"
            :drop-target-id="dropTargetId"
            @select="selectNode"
            @request-insert="requestInsert"
          />
          <p
            v-if="form.tree && !form.tree.childs.length"
            class="mt-3 text-sm text-n-slate-11"
          >
            {{ t('TELETEL_FLOWS.BUILDER.CANVAS_EMPTY') }}
          </p>
        </div>

        <div
          class="absolute flex items-center gap-1 p-1 border rounded-lg shadow-sm bottom-3 ltr:right-3 rtl:left-3 border-n-weak bg-n-solid-2"
        >
          <button
            type="button"
            class="grid rounded w-7 h-7 place-items-center text-n-slate-12 hover:bg-n-slate-3"
            :title="t('TELETEL_FLOWS.BUILDER.ZOOM_OUT')"
            :aria-label="t('TELETEL_FLOWS.BUILDER.ZOOM_OUT')"
            @click="zoomOut"
          >
            <Icon icon="i-lucide-zoom-out" class="size-4" />
          </button>
          <button
            type="button"
            class="px-1.5 h-7 text-xs tabular-nums rounded text-n-slate-11 hover:bg-n-slate-3"
            :title="t('TELETEL_FLOWS.BUILDER.ZOOM_RESET')"
            :aria-label="t('TELETEL_FLOWS.BUILDER.ZOOM_RESET')"
            @click="resetView"
          >
            {{ zoomLabel }}
          </button>
          <button
            type="button"
            class="grid rounded w-7 h-7 place-items-center text-n-slate-12 hover:bg-n-slate-3"
            :title="t('TELETEL_FLOWS.BUILDER.ZOOM_IN')"
            :aria-label="t('TELETEL_FLOWS.BUILDER.ZOOM_IN')"
            @click="zoomIn"
          >
            <Icon icon="i-lucide-zoom-in" class="size-4" />
          </button>
        </div>
      </main>

      <aside
        class="w-80 shrink-0 overflow-auto border rounded-xl border-n-weak p-3"
      >
        <h2 class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.BUILDER.SETTINGS') }}
        </h2>
        <FlowSettings
          v-if="selectedNode && selectedNode.customType === 'Trigger'"
          :form="form"
        />
        <NodeSettings
          v-else-if="selectedNode"
          :node="selectedNode"
          :tree="form.tree"
          @delete-node="deleteNode"
        />
        <p v-else class="text-sm text-n-slate-11">
          {{ t('TELETEL_FLOWS.BUILDER.SELECT_NODE') }}
        </p>
      </aside>
    </div>
  </div>
</template>
